import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef, MatHeaderRow,
    MatHeaderRowDef, MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {map} from 'rxjs/operators';
import {BreadCrumb} from '../../models/breadcrumb';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {Facility} from '../../models/facility';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Subscription} from 'rxjs';
import {Auth, authState} from '@angular/fire/auth';
import {collection, collectionSnapshots, deleteDoc, doc, Firestore, getDocs, orderBy, query, where} from '@angular/fire/firestore';
import {BreadcrumbsComponent} from '../breadcrumbs/breadcrumbs.component';
import {ProgressComponent} from '../progress/progress.component';
import {MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle} from '@angular/material/card';
import {MatButton, MatMiniFabButton} from '@angular/material/button';
import {MatInput} from '@angular/material/input';
import {MatFormField} from '@angular/material/form-field';
import {I18nPluralPipe} from '@angular/common';
import {DateFormatPipe} from '../../pipes/date-format.pipe';

@Component({
    selector: 'app-admin-facilities',
    templateUrl: './facilities.component.html',
    styleUrls: ['./facilities.component.css'],
    imports: [
        BreadcrumbsComponent,
        ProgressComponent,
        MatCard,
        MatCardTitle,
        MatCardSubtitle,
        MatCardHeader,
        RouterLink,
        MatButton,
        MatFormField,
        MatInput,
        MatTable,
        MatSort,
        MatSortHeader,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCell,
        MatCellDef,
        MatMiniFabButton,
        MatHeaderRowDef,
        MatRow,
        MatPaginator,
        MatRowDef,
        MatHeaderRow,
        MatCardContent,
        I18nPluralPipe,
        DateFormatPipe
    ],
    standalone: true
})
export class FacilitiesComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    accountId: string;
    dataSource: MatTableDataSource<Facility>;
    breadcrumbs: BreadCrumb[];
    displayedColumns: string[] = ['name', 'address', 'created', 'actions'];
    facilityPluralMapping = {
        '=0': '0 Facilities',
        '=1': '1 Facility',
        other: '# Facilities'
    };
    admin: boolean;
    private subscription: Subscription;
    private locationSubscription: Subscription;
    private authSubscription: Subscription;

    constructor(
        private firestore: Firestore,
        private route: ActivatedRoute,
        private snackBar: MatSnackBar,
        private fireAuth: Auth) {
    }

    ngOnInit() {
        this.breadcrumbs = [
            {url: ['/'], title: 'Accounts'},
            {url: null, title: 'Facilities'},
        ];
        this.authSubscription = authState(this.fireAuth).subscribe(async (value) => {
            const result = await value.getIdTokenResult();
            this.admin = Boolean(result.claims.admin);
        });
        this.route.paramMap.subscribe(params => {
            this.accountId = params.get('accountId');
            this.subscription = collectionSnapshots(
                query(
                    collection(this.firestore, 'facilities',),
                    where('accountId', '==', this.accountId),
                    orderBy('name', 'asc')
                )
            ).pipe(
                map(facilities =>
                    facilities.map(facility => {
                            const data = facility.data() as Facility;
                            const id = facility.id;
                            return {id, ...data};
                        }
                    )
                )
            ).subscribe(data => {
                this.dataSource = new MatTableDataSource(data);
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
            });
        });
    }

    ngAfterViewInit() {
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.locationSubscription && !this.locationSubscription.closed) {
            this.locationSubscription.unsubscribe();
        }
        this.authSubscription.unsubscribe();
    }

    search(event: any) {
        this.dataSource.filter = event.target.value.trim().toLowerCase();
    }

    async deleteFacility(facility: Facility): Promise<void> {
        if (!this.admin) {
            this.snackBar.open(
                'Permission Denied. Please contact an administrator',
                null,
                {duration: 3000}
            );
            return;
        }

        try {
            const facilityDocRef = doc(this.firestore, 'facilities', facility.id);
            const locationsColRef = collection(facilityDocRef, 'locations');
            const locationsSnap = await getDocs(locationsColRef);

            if (!locationsSnap.empty) {
                this.snackBar.open(
                    `Please delete all locations from facility ${facility.name} before deleting it`,
                    null,
                    {duration: 5000}
                );
                return;
            }
            const confirmed = confirm(`Are you sure you want to delete ${facility.name}?`);
            if (!confirmed) {
                return;
            }
            const facilitiesColRef = collection(this.firestore, 'facilities');
            const facilityRefForQuery = doc(facilitiesColRef, facility.id);
            const itemsColRef = collection(this.firestore, 'items');
            const itemsQuery = query(
                itemsColRef,
                where('facility', '==', facilityRefForQuery)
            );
            const itemsSnap = await getDocs(itemsQuery);

            if (!itemsSnap.empty) {
                this.snackBar.open(
                    `Please remove all items from facility ${facility.name} before deleting it`,
                    null,
                    {duration: 5000}
                );
                return;
            }
            await deleteDoc(facilityDocRef);
            this.snackBar.open('Facility Deleted', null, {duration: 3000});
        } catch (err) {
            console.error(err);
            this.snackBar.open(
                `Error removing ${facility.name}`,
                null,
                {duration: 5000}
            );
        }
    }
}
